import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useBreakpoints from '../../../../common/hooks/useBreakpoints';
import ExpandablePanel from '../../../../common/widgets/ExpandablePanel';
import Icons from '../../../../common/widgets/Icons';
import { CustomerInfoContext } from '../../contexts/CustomerInfoContext';
import { resources } from '../../resources/resources';
import CompanyInfo from './components/CompanyInfo';
import Contact from './components/Contact';
import DownloadApp from './components/DownloadApp';
import Legal from './components/Legal';
import LitigationBadges from './components/LitigationBadges';
import MyAccount from './components/MyAccount';

type FooterSection = 'myAccount' | 'legal' | 'contact' | 'company';

export default function Footer() {
  const { t: translate } = useTranslation();
  const { isMobile } = useBreakpoints();
  const { customerInfo } = useContext(CustomerInfoContext);
  const [expandedSection, setExpandedSection] = useState<FooterSection | null>(
    null,
  );

  const getPanelProps = (section: FooterSection) => ({
    isExpanded: expandedSection === section,
    onExpandedChange: (expanded: boolean) => {
      setExpandedSection(expanded ? section : null);
    },
  });

  return (
    <footer>
      {/*Desktop and tablet*/}
      <div className="footer-top">
        <div className="container">
          <div
            className={`footer-top-content ${
              customerInfo.isSavingsUser ? 'footer-top-content--compact' : ''
            }`.trim()}
          >
            {!customerInfo.isSavingsUser && (
              <div className="footer-nav-column">
                <div className="footer-title">
                  {translate(resources.footer.downloadApp)}
                </div>
                <DownloadApp />
              </div>
            )}
            <div className="footer-nav-column">
              {!isMobile ? (
                <>
                  <div className="footer-title">
                    {translate(resources.footer.myAccount)}
                    <span>
                      <Icons.ArrowFooter />
                    </span>
                  </div>
                  <MyAccount />
                </>
              ) : (
                <ExpandablePanel
                  {...getPanelProps('myAccount')}
                  expandOn="header"
                  arrowType={'footer'}
                  title={
                    <div className="footer-title">
                      {translate(resources.footer.myAccount)}
                    </div>
                  }
                >
                  <MyAccount />
                </ExpandablePanel>
              )}
            </div>
            <div className="footer-nav-column">
              {!isMobile ? (
                <>
                  <div className="footer-title expand-example-title">
                    {translate(resources.footer.title.legal)}
                    <span>
                      <Icons.ArrowFooter />
                    </span>
                  </div>
                  <Legal />
                </>
              ) : (
                <ExpandablePanel
                  {...getPanelProps('legal')}
                  expandOn={'header'}
                  arrowType={'footer'}
                  title={
                    <div className="footer-title">
                      {translate(resources.footer.title.legal)}
                    </div>
                  }
                >
                  <Legal />
                </ExpandablePanel>
              )}
            </div>
            <div className="footer-nav-column">
              {!isMobile ? (
                <>
                  <div className="footer-title ">
                    {translate(resources.footer.title.contact)}
                    <span>
                      <Icons.ArrowFooter />
                    </span>
                  </div>
                  <Contact />
                </>
              ) : (
                <ExpandablePanel
                  {...getPanelProps('contact')}
                  expandOn={'header'}
                  arrowType={'footer'}
                  title={
                    <div className="footer-title">
                      {translate(resources.footer.title.contact)}
                    </div>
                  }
                >
                  <Contact />
                </ExpandablePanel>
              )}
            </div>
            <div className="footer-nav-column">
              {!isMobile ? (
                <>
                  <div className="footer-title expand-example-title">
                    {translate(resources.footer.title.company)}
                    <span>
                      <Icons.ArrowFooter />
                    </span>
                  </div>
                  <CompanyInfo />
                  <LitigationBadges />
                </>
              ) : (
                <ExpandablePanel
                  {...getPanelProps('company')}
                  expandOn={'header'}
                  arrowType={'footer'}
                  title={
                    <div className="footer-title expand-example-title">
                      {translate(resources.footer.title.company)}
                    </div>
                  }
                >
                  <CompanyInfo />
                </ExpandablePanel>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="container">
          <div className="footer-bottom-content">
            {isMobile && <LitigationBadges />}
          </div>
        </div>
      </div>
    </footer>
  );
}
